import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Text from "../components/text";

const FinalList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedColleges, setSelectedColleges] = useState([]);
  const { navigationfrom } = location.state || {};
  const [userData, setUserData] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    const { selectedColleges: collegesFromState = [], formData } =
      location.state || {};
    setSelectedColleges(collegesFromState);
    if (formData) {
      setUserData(formData);
    } else {
      const userDataFromStorage = localStorage.getItem("userData");
      if (userDataFromStorage) {
        try {
          setUserData(JSON.parse(userDataFromStorage));
        } catch (error) {
          console.error("Error parsing user data:", error);
        }
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (navigationfrom) {
      setShowPopup(true);
    }
  }, [navigationfrom]);

  const handleBack = () => {
    window.history.back();
  };

  const handleGeneratePdf = () => {
    setIsGeneratingPdf(true);
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: "a4",
    });

    pdf.setFontSize(16);
    pdf.text(
      Text.PrintPdf.CollegeSelectionReport,
      pdf.internal.pageSize.getWidth() / 2,
      40,
      { align: "center" }
    );

    pdf.setFontSize(12);
    pdf.text(Text.PrintPdf.StudentInfo, 40, 60);
    pdf.setFontSize(10);
    pdf.text(
      `${Text.PrintPdf.Gender}: ${userData.gender || "Not provided"}`,
      40,
      80
    );
    pdf.text(
      `${Text.PrintPdf.Rank}: ${userData.rank || "Not provided"}`,
      40,
      90
    );
    pdf.text(
      `${Text.PrintPdf.Category}: ${
        userData.caste?.toUpperCase() || "Not provided"
      }`,
      40,
      100
    );

    if (selectedColleges.length > 0) {
      const rankKey = getRankKey();
      autoTable(pdf, {
        startY: 120,
        head: [
          ["#", "Code", "College Name", "Location", "Branch", "Close Rank"],
        ],
        body: selectedColleges.map((college, index) => [
          index + 1,
          college.inst_code,
          college.inst_name,
          college.place,
          college.branch_code,
          college[rankKey] || "N/A",
        ]),
        theme: "grid",
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 30 },
          2: { cellWidth: "auto" },
          3: { cellWidth: "auto" },
          4: { cellWidth: 30 },
          5: { cellWidth: 40 },
        },
      });
    } else {
      pdf.setFontSize(10);
      pdf.text("No colleges selected.", 40, 120);
    }

    pdf.setFontSize(8);
    pdf.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pdf.internal.pageSize.getWidth() / 2,
      pdf.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );

    pdf.save("College_Selection_Report.pdf");
    setIsGeneratingPdf(false);
  };

  const getRankKey = () => {
    if (!userData.caste || !userData.gender) return "";
    return `${userData.caste}_${userData.gender === "Male" ? "boys" : "girls"}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-3">
              {Text.PrintPdf.Success}
            </h3>
            <p className="text-gray-700 mb-4">[{Text.PrintPdf.Note1}]</p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowPopup(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {Text.PrintPdf.Ok}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            {Text.PrintPdf.CollegeSelectionReport}
          </h2>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-blue-200 pb-2">
              {Text.PrintPdf.StudentInfo}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="flex items-center mt-2">
                  <span className="font-medium text-gray-700 w-20">
                    {Text.PrintPdf.Gender}
                  </span>
                  <span className="text-gray-800">
                    {userData.gender || "Not provided"}
                  </span>
                </p>
              </div>
              <div>
                <p className="flex items-center">
                  <span className="font-medium text-gray-700 w-20">
                    {Text.PrintPdf.Rank}
                  </span>
                  <span className="text-gray-800">
                    {userData.rank || "Not provided"}
                  </span>
                </p>
                <p className="flex items-center mt-2">
                  <span className="font-medium text-gray-700 w-20">
                    {Text.PrintPdf.Category}
                  </span>
                  <span className="text-gray-800">
                    {userData.caste?.toUpperCase() || "Not provided"}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-2">
              {Text.PrintPdf.SelectedColleges}
            </h3>
            {selectedColleges.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Code
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell"
                      >
                        College Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell"
                      >
                        Location
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Branch
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Close Rank
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedColleges.map((college, index) => {
                      const rankKey = getRankKey();
                      return (
                        <tr
                          key={index}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-800">
                            {index + 1}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                            {college.inst_code}
                            <div className="md:hidden">
                              <div className="text-xs text-gray-600 mt-1">
                                {college.inst_name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {college.place}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-700 hidden md:table-cell max-w-[200px] truncate">
                            {college.inst_name}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-700 hidden md:table-cell">
                            {college.place}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-700">
                            {college.branch_code}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                            {college[rankKey] || "N/A"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {Text.PrintPdf.Oops}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{Text.PrintPdf.Note2}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="text-center text-gray-500 text-sm border-t border-gray-200 pt-4">
            Generated on: {new Date().toLocaleString()}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-sm"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() =>
              navigate("/review-report", {
                state: { selectedColleges, formData: userData },
              })
            }
            className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
          >
            Review Report
          </button>
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={selectedColleges.length === 0 || isGeneratingPdf}
            className={`px-6 py-2.5 flex items-center justify-center rounded-lg shadow-sm ${
              selectedColleges.length === 0 || isGeneratingPdf
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 transition-colors"
            } text-white`}
          >
            {isGeneratingPdf ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {Text.PrintPdf.GeneraingPdf}
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                {Text.PrintPdf.DownloadPdf}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalList;
